/**
 * テスト詳細API
 */
import { Component, Vue } from 'vue-property-decorator'
import {
  ApiResponseOriginalCurriculumInfo,
  ApiResponseOriginalCurriculumUnit,
  ApiOriginalCurriculumUnit,
  ApiResponseOriginalCurriculumMonth,
} from '@/models/apiResponses/originalCurriculums'
import { ApiResponseStudentList } from '@/models/apiResponses/students'
import { StudyDetailSearchParamsType } from '@/models/studyDetailModel'
import { StudentSearchParamsType } from '@/models/studentListModel'
import { UserRoles } from '@/const/UserRoles'
import { StudyContentSetting } from '@/const/ApiSettings'

@Component
export default class OriginalCurriculumsApi extends Vue {
  /**
   * カリキュラム情報取得
   * @param id オリジナルカリキュラムID
   * @returns カリキュラム情報
   */
  protected async getOriginalCurriculumInfo(id: number): Promise<ApiResponseOriginalCurriculumInfo> {
    const { data }: { data: ApiResponseOriginalCurriculumInfo } = await Vue.prototype.$http.httpWithToken.get(
      `/v2/original_curriculums/${id}/info`
    )

    return data
  }

  /**
   * テスト詳細一覧取得
   * @param id オリジナルカリキュラムID
   * @param params 検索パラメーター群
   * @param role ユーザー権限
   * @returns テスト詳細一覧
   */
  protected async getOriginalCurriculumUnitList(
    id: number,
    params: StudyDetailSearchParamsType,
    role: UserRoles
  ): Promise<ApiResponseOriginalCurriculumUnit[]> {
    const { data }: { data: ApiResponseOriginalCurriculumUnit[] } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/original_curriculums/${id}/unit_list`, { params: params })
      .catch(() => ({ data: [] }))

    return data
  }

  /**
   * テスト詳細一覧更新
   * @param settings テスト詳細設定
   * @param role ユーザー権限
   */
  protected async updateOriginalCurriculumUnitList(
    settings: ApiOriginalCurriculumUnit[],
    role: UserRoles
  ): Promise<void> {
    await Vue.prototype.$http.httpWithToken.post(`/v2/${role}/curriculum_s_unit_settings`, {
      sUnitSettings: settings,
    })
  }

  /**
   * 実施月一覧取得
   * @param years 実施年度
   * @returns 実施月一覧
   */
  protected async getMonths(years: number): Promise<ApiResponseOriginalCurriculumMonth[]> {
    const { data }: { data: ApiResponseOriginalCurriculumMonth[] } = await Vue.prototype.$http.httpWithToken.get(
      '/v2/original_curriculums/months_by_study_mode_and_years',
      {
        params: {
          years: years,
          functionType: StudyContentSetting.DEFAULT_FUNCTION_TYPE,
          studyMode: StudyContentSetting.DEFAULT_STUDY_MODE,
        },
      }
    )

    return data
  }

  /**
   * OriginalCurriculumSettingUserAvailables 可否一覧エクセルアップロード
   * @param file エクセルファイル
   */
  protected async importOriginalCurriculumSettingUserAvailables(
    originalCurriculumId: number,
    file: File
  ): Promise<void> {
    const headers = { 'Content-Type': 'multipart/form-data' }
    const params = new FormData()
    params.append('excel', file)
    await Vue.prototype.$http.httpWithToken.post(
      `/v2/admin/original_curriculum_setting_user_availables/import/${originalCurriculumId}`,
      params,
      {
        headers: headers,
      }
    )
  }

  /**
   * 生徒受験可否一覧のエクセルダウンロード
   * @param params 検索パラメーター群
   * @returns エクセルデータ
   */
  protected async exportStudentAvailableExcel(params: StudentSearchParamsType, role: UserRoles): Promise<Blob> {
    const { data }: { data: Blob } = await Vue.prototype.$http.httpWithToken.get(
      `/v2/${role}/original_curriculum_settings/export_student_available_excel`,
      {
        params: params,
        responseType: 'blob',
        dataType: 'binary',
      }
    )

    return data
  }

  /**
   * OriginalCurriculumSettingUserAvailables 可否情報取得
   * @param params 検索パラメーター群
   * @returns OriginalCurriculumSettingUserAvailables 可否一覧
   */
  protected async getUserAvailableData(
    params: StudentSearchParamsType,
    role: UserRoles
  ): Promise<ApiResponseStudentList> {
    const { data }: { data: ApiResponseStudentList } = await Vue.prototype.$http.httpWithToken.get(
      `/v2/${role}/original_curriculum_settings/available_users`,
      {
        params: params,
      }
    )

    return data
  }
}
